import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "utils";

import { Button, Icon, OverflowEllipsisWithTooltip, Popover, Typography } from "components";

export type FarmSwitchFarm = {
  id: string;
  name: string;
  openTaskCount?: number;
};

type FarmSwitchProps = {
  farms: FarmSwitchFarm[];
  activeFarmId?: string;
  /** Shows the active farm without the count and keeps the menu closed, while the farm list is not available. */
  disabled?: boolean;
  onSelect: (farmId: string) => void;
  onEdit: (farmId: string) => void;
  onDelete: (farmId: string) => void;
  onAdd: () => void;
};

const MENU_ITEM_SELECTOR = '[role="menuitem"], [role="menuitemradio"]';

export const FarmSwitch = ({ farms, activeFarmId, disabled = false, onSelect, onEdit, onDelete, onAdd }: FarmSwitchProps) => {
  const { t } = useTranslation(["nav", "farm"]);
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const activeFarm = farms.find((farm) => farm.id === activeFarmId) ?? farms[0];
  const anyFarmHasOpenTasks = farms.some((farm) => (farm.openTaskCount ?? 0) > 0);

  // the popover closes on outside clicks by itself, Escape is ours
  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setIsOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isOpen]);

  // the menu renders at the end of the document, so the keyboard would never reach it on its own
  const focusActiveItem = () => {
    const menu = menuRef.current;
    const activeItem = menu?.querySelector<HTMLElement>('[role="menuitemradio"][aria-checked="true"]') ?? menu?.querySelector<HTMLElement>(MENU_ITEM_SELECTOR);
    activeItem?.focus();
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const menu = menuRef.current;
    if (!menu) return;

    const items = Array.from(menu.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR));
    const current = items.indexOf(document.activeElement as HTMLElement);

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      const fallback = step > 0 ? 0 : items.length - 1;
      items[current < 0 ? fallback : (current + step + items.length) % items.length]?.focus();
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      items[event.key === "Home" ? 0 : items.length - 1]?.focus();
    } else if (event.key === "Tab") {
      const leaving = event.shiftKey ? current === 0 : current === items.length - 1;
      if (leaving) {
        event.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    }
  };

  // the focused entry disappears with the menu, so the focus goes back to the trigger; drawers and modals take it from there
  const closeThen = (action: () => void) => () => {
    setIsOpen(false);
    triggerRef.current?.focus();
    action();
  };

  const openFromArrowKey = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    setIsOpen(true);
  };

  // the count is hidden while the list is not available, so the name leaves it out as well
  const triggerLabel = [
    `${t("farmSwitch.switchFarm")}: ${activeFarm?.name ?? ""}`,
    ...(disabled ? [] : [t("farmSwitch.farmsCount", { count: farms.length })]),
    ...(anyFarmHasOpenTasks ? [t("farmSwitch.openTasksOnFarms")] : []),
  ].join(", ");

  const menu = (
    <div
      ref={menuRef}
      className="farm-switch-menu"
      role="menu"
      aria-label={t("farmSwitch.yourFarms")}
      data-testid="farm-switch-menu"
      onKeyDown={handleMenuKeyDown}
    >
      <Typography variant="paragraph-s" className="farm-switch-heading">
        {t("farmSwitch.yourFarms")}
      </Typography>
      <div role="group" aria-label={t("farmSwitch.yourFarms")}>
        {farms.map((farm, index) => {
          const active = farm.id === activeFarm?.id;
          const openTaskCount = farm.openTaskCount ?? 0;

          return (
            <div key={farm.id} className={`farm-switch-row${active ? " active" : ""}`} data-testid={`farm-switch-item-${index}`}>
              <Button
                role="menuitemradio"
                aria-checked={active}
                className="farm-switch-row-button"
                mixpanelText="Farm switch: select farm"
                onClick={closeThen(() => onSelect(farm.id))}
              >
                {/* only the active farm carries a mark; an empty ring on the others would read as a multi-select */}
                <span className={`farm-switch-check${active ? " active" : ""}`} aria-hidden>
                  {active ? <Icon iconName="tabler/check" size="xs" strokeWidth={2.5} dataTestId="farm-switch-item-active-check" /> : null}
                </span>
                <OverflowEllipsisWithTooltip className="farm-switch-row-name" title={farm.name}>
                  {farm.name}
                </OverflowEllipsisWithTooltip>
                {openTaskCount > 0 ? (
                  <span
                    className="farm-switch-open-tasks"
                    title={t("farmSwitch.openTasksTitle", { count: openTaskCount })}
                    data-testid={`farm-switch-item-${index}-open-tasks`}
                  >
                    <span className="farm-switch-open-tasks-dot" aria-hidden />
                    {t("farmSwitch.openTasks", { count: openTaskCount })}
                  </span>
                ) : null}
              </Button>
              <span className="farm-switch-row-actions">
                <Button
                  role="menuitem"
                  className="farm-switch-action"
                  aria-label={t("farmSwitch.editFarm", { farm: farm.name })}
                  title={t("farm:farmContextMenu.editFarm")}
                  dataTestId={`farm-switch-edit-${index}`}
                  mixpanelText="Farm switch: edit farm"
                  icon={<Icon iconName="tabler/edit" size="s" />}
                  onClick={closeThen(() => onEdit(farm.id))}
                />
                <Button
                  role="menuitem"
                  className="farm-switch-action delete"
                  aria-label={t("farmSwitch.deleteFarm", { farm: farm.name })}
                  title={t("farm:farmContextMenu.deleteFarm")}
                  dataTestId={`farm-switch-delete-${index}`}
                  mixpanelText="Farm switch: delete farm"
                  icon={<Icon iconName="tabler/trash" size="s" />}
                  onClick={closeThen(() => onDelete(farm.id))}
                />
              </span>
            </div>
          );
        })}
      </div>
      <div className="farm-switch-divider" aria-hidden />
      <Button
        role="menuitem"
        className="farm-switch-add"
        dataTestId="farm-switch-add"
        mixpanelText="Farm switch: add farm"
        icon={<Icon iconName="klim/plus" size={20} />}
        iconLeft
        onClick={closeThen(onAdd)}
      >
        <span className="farm-switch-row-name">{t("farm:farmContextMenu.addFarm")}</span>
      </Button>
    </div>
  );

  return (
    <Popover
      className="farm-switch-popover"
      placement="bottomLeft"
      arrow={false}
      trigger="click"
      open={isOpen}
      onOpenChange={setIsOpen}
      afterOpenChange={(open) => open && focusActiveItem()}
      content={menu}
      align={{ offset: [0, 8] }}
    >
      <Button
        ref={triggerRef}
        className={`farm-switch${isOpen ? " open" : ""}`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={triggerLabel}
        disabled={disabled}
        dataTestId="farm-switch"
        mixpanelText="Farm switch: open"
        onKeyDown={openFromArrowKey}
      >
        {anyFarmHasOpenTasks ? <span className="farm-switch-dot" title={t("farmSwitch.openTasksOnFarms")} aria-hidden data-testid="farm-switch-dot" /> : null}
        <Icon iconName="klim/field" size={20} className="farm-switch-icon" />
        <OverflowEllipsisWithTooltip className="farm-switch-name" title={activeFarm?.name ?? ""}>
          {activeFarm?.name}
        </OverflowEllipsisWithTooltip>
        {disabled ? null : (
          <span className="farm-switch-count" data-testid="farm-switch-count">
            {farms.length}
          </span>
        )}
        <Icon iconName="tabler/chevron-down" size="m-light" className="farm-switch-chevron" />
      </Button>
    </Popover>
  );
};
